import ImageThumbnail from '@/components/media/ImageThumbnail'
import LazyImageEditorDialog from '@/components/media/LazyImageEditorDialog'
import ImageUploader from '@/components/media/ImageUploader'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import AutoComplete from '@/components/form/AutoComplete'
import FieldInfo from '@/components/form/FieldInfo'
import { Button } from '@/components/ui/button'
import { Dropzone } from '@/components/ui/dropzone'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import withSuspense from '@/hooks/withSuspense'
import { useAbility, useIsReadOnly } from '@/lib/casl'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useFormContext } from '@/lib/form-contexts'
import { canEditMedia, mergeEditedMedia, selectFirstValidAssetMedia } from '@/lib/media-editing'
import {
  PhotoManagerDefaultUploadDestination,
  PhotoManagerSelectionMode,
} from '@/lib/photo-manager-config'
import {
  designCollectionsFilters,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { designProductsFilters, useProducts } from '@/lib/services/products/products.query'
import { cn } from '@/lib/utils'
import { closestCorners, DndContext } from '@dnd-kit/core'
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useStore } from '@tanstack/react-form'
import _get from 'lodash/get'
import {
  ChevronDown,
  Grip,
  ImagePlus,
  Images,
  MessageCircleHeart,
  Pencil,
  PlusIcon,
  Rows3,
  ShoppingBag,
  Star,
  X,
} from 'lucide-react'
import { useEffect, useId, useMemo, useState } from 'react'
import { toast } from 'sonner'
import CollectionStorefrontVisibilityIndicator, {
  getCollectionStorefrontVisibilityMessage,
} from './CollectionStorefrontVisibilityIndicator'
import {
  FEATURED_COLLECTION_BLOCK_TYPE,
  CONTACT_US_WIDGET_BLOCK_TYPE,
  CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE,
  DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT,
  DEFAULT_CONTACT_US_WIDGET_MESSAGE,
  DEFAULT_CONTACT_US_WIDGET_TITLE,
  DEFAULT_REVIEWS_WIDGET_MESSAGE,
  DEFAULT_REVIEWS_WIDGET_TITLE,
  FEATURED_PRODUCT_BLOCK_TYPE,
  GALLERY_WIDGET_BLOCK_TYPE,
  LEGACY_FEATURED_COLLECTION_BLOCK_TYPE,
  applyMainBlockSortOrder,
  buildFeaturedCollectionOptions,
  getCollectionStorefrontVisibilityIssue,
  hasSeasonalFeaturedCollectionSelection,
  isFeaturedContentWidgetBlock,
} from './designForm.utils'
import {
  EMPTY_COLLECTION_MEDIA_DRAFTS,
  getCollectionMediaId,
  getCollectionMediaWithDraft,
  hasCollectionMediaDraft,
} from './collectionMediaDrafts'
import { PreviewDraftTextareaField, PreviewDraftTextField } from './PreviewDraftFields'

const PAGE_CONFIG = {
  '/': {
    label: 'Home',
    maxFeaturedCollectionWidgets: null,
    allowGalleryWidget: true,
    allowContactUsWidget: true,
    allowReviewsWidget: true,
    emptyState:
      'No featured content selected yet. Your storefront will show the full product list automatically.',
  },
  '/about-us': {
    label: 'About Us',
    maxFeaturedCollectionWidgets: 1,
    allowGalleryWidget: true,
    allowContactUsWidget: true,
    allowReviewsWidget: true,
    emptyState: 'No featured content selected yet.',
  },
  '/gallery': {
    label: 'Gallery',
    maxFeaturedCollectionWidgets: 1,
    allowGalleryWidget: false,
    allowContactUsWidget: true,
    allowReviewsWidget: true,
    emptyState: 'No featured content selected yet.',
  },
  '/contact-us': {
    label: 'Contact',
    maxFeaturedCollectionWidgets: 1,
    allowGalleryWidget: true,
    allowContactUsWidget: false,
    allowReviewsWidget: true,
    emptyState: 'No featured content selected yet.',
  },
  '/reviews': {
    label: 'Reviews',
    maxFeaturedCollectionWidgets: 1,
    allowGalleryWidget: true,
    allowContactUsWidget: true,
    allowReviewsWidget: false,
    emptyState: 'No featured content selected yet.',
  },
}

const pageConfigFor = (pageSlug) => PAGE_CONFIG[pageSlug] ?? PAGE_CONFIG['/']
const blockPathPrefix = (pageSlug) => `pages.${pageSlug}.blocks`
const nameForPage = (pageSlug, str) => `${blockPathPrefix(pageSlug)}.${str}`
const mainForPage = (pageSlug, str) => nameForPage(pageSlug, `main.${str}`)

const isFeaturedCollectionWidget = (block) =>
  block?.type === FEATURED_COLLECTION_BLOCK_TYPE ||
  block?.type === LEGACY_FEATURED_COLLECTION_BLOCK_TYPE
const isGalleryWidget = (block) => block?.type === GALLERY_WIDGET_BLOCK_TYPE
const isContactUsWidget = (block) => block?.type === CONTACT_US_WIDGET_BLOCK_TYPE
const isReviewsWidget = (block) => block?.type === CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE
const getAlbumOptionId = (entry) => Number(entry?.album_id ?? entry?.album?.id ?? entry?.id)
const MAX_SEARCHABLE_PRODUCT_OPTIONS = 25

const getFeaturedCollectionLimitMessage = ({ label, maxFeaturedCollectionWidgets }) => {
  if (maxFeaturedCollectionWidgets === null) {
    return null
  }

  if (maxFeaturedCollectionWidgets === 1) {
    return `${label} can include one Featured Collection widget.`
  }

  return `${label} can include ${maxFeaturedCollectionWidgets} Featured Collection widgets.`
}

const createWidgetId = (type) => `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const createNewFeaturedCollection = () => {
  // TODO: alternate layouts and alternate left/right image alignment
  return {
    id: createWidgetId(FEATURED_COLLECTION_BLOCK_TYPE),
    type: FEATURED_COLLECTION_BLOCK_TYPE,
    is_enabled: true,
    collections: [{ collection_id: null }],
    props: {
      layout: 'carousel',
      size: 'sm',
      imageAlign: 'left',
      title: '',
      ctaText: '',
      ctaButtonText: '',
    },
  }
}

const createNewGalleryWidget = () => ({
  id: createWidgetId(GALLERY_WIDGET_BLOCK_TYPE),
  type: GALLERY_WIDGET_BLOCK_TYPE,
  is_enabled: true,
  props: {
    layout: 'carousel',
    size: 'sm',
    imageAlign: 'left',
    focusAlbumId: null,
  },
})

const createNewFeaturedProduct = () => ({
  id: createWidgetId(FEATURED_PRODUCT_BLOCK_TYPE),
  type: FEATURED_PRODUCT_BLOCK_TYPE,
  is_enabled: true,
  products: [{ product_id: null }],
  props: {
    layout: 'split',
    imageAlign: 'left',
    title: '',
    ctaText: '',
    ctaButtonText: '',
  },
})

const createNewContactUsWidget = () => ({
  id: createWidgetId(CONTACT_US_WIDGET_BLOCK_TYPE),
  type: CONTACT_US_WIDGET_BLOCK_TYPE,
  is_enabled: true,
  props: {
    title: DEFAULT_CONTACT_US_WIDGET_TITLE,
    ctaText: DEFAULT_CONTACT_US_WIDGET_MESSAGE,
    ctaButtonText: DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT,
  },
})

const createNewReviewsWidget = () => ({
  id: createWidgetId(CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE),
  type: CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE,
  is_enabled: true,
  props: {
    title: DEFAULT_REVIEWS_WIDGET_TITLE,
    ctaText: DEFAULT_REVIEWS_WIDGET_MESSAGE,
  },
})

const getGalleryAlbumOptions = (albums = []) =>
  (Array.isArray(albums) ? albums : [])
    .map((entry) => {
      const id = getAlbumOptionId(entry)

      if (!Number.isFinite(id) || id <= 0) {
        return null
      }

      return {
        id,
        name: entry?.album?.name || entry?.name || `Album ${id}`,
      }
    })
    .filter(Boolean)

const getSortableId = (block, index) => String(block?.id ?? `${block?.type ?? 'widget'}-${index}`)

const getWidgetEntries = (mainBlocks = []) =>
  (Array.isArray(mainBlocks) ? mainBlocks : [])
    .map((item, index) => ({
      item,
      index,
      sortableId: getSortableId(item, index),
    }))
    .filter(({ item }) => isFeaturedContentWidgetBlock(item))

const setMainBlocks = (form, mainPath, nextBlocks) => {
  form.setFieldValue(mainPath, applyMainBlockSortOrder(nextBlocks), {
    dontValidate: true,
  })
}

const getMainBlocks = (form, mainPath) => {
  const blocks = form.getFieldValue(mainPath)
  return Array.isArray(blocks) ? blocks : []
}

const findLastWidgetIndex = (blocks) =>
  blocks.reduce(
    (lastIndex, block, index) => (isFeaturedContentWidgetBlock(block) ? index : lastIndex),
    -1
  )

const countWidgetBlocks = (blocks, predicate) =>
  getWidgetEntries(blocks).filter(({ item }) => predicate(item)).length

const getFirstFeaturedCollectionEntry = (blocks) => {
  const featuredCollectionEntries = getWidgetEntries(blocks).filter(({ item }) =>
    isFeaturedCollectionWidget(item)
  )

  return (
    featuredCollectionEntries.find(({ item }) => hasSeasonalFeaturedCollectionSelection(item)) ??
    featuredCollectionEntries[0] ??
    null
  )
}

const flushActiveDraftField = () => {
  if (typeof document === 'undefined') {
    return
  }

  const activeElement = document.activeElement

  if (activeElement && typeof activeElement.blur === 'function') {
    activeElement.blur()
  }
}

const canAddFeaturedCollectionToBlocks = (blocks, maxFeaturedCollectionWidgets) => {
  if (maxFeaturedCollectionWidgets === null) {
    return true
  }

  const featuredCollectionCount = countWidgetBlocks(blocks, isFeaturedCollectionWidget)

  return featuredCollectionCount < maxFeaturedCollectionWidgets
}

const canAddOnePerPageWidgetToBlocks = (blocks, isWidget) => countWidgetBlocks(blocks, isWidget) < 1

const appendWidgetBlock = (form, mainPath, nextWidget, canAppend = () => true) => {
  flushActiveDraftField()

  const blocks = [...getMainBlocks(form, mainPath)]

  if (!canAppend(blocks)) {
    return
  }

  const lastWidgetIndex = findLastWidgetIndex(blocks)
  const insertIndex = lastWidgetIndex >= 0 ? lastWidgetIndex + 1 : blocks.length

  blocks.splice(insertIndex, 0, nextWidget)
  setMainBlocks(form, mainPath, blocks)
}

const removeWidgetBlock = (form, mainPath, sortableId) => {
  flushActiveDraftField()

  const nextBlocks = getMainBlocks(form, mainPath).filter((block, index) => {
    if (!isFeaturedContentWidgetBlock(block)) {
      return true
    }

    return getSortableId(block, index) !== sortableId
  })

  setMainBlocks(form, mainPath, nextBlocks)
}

const reorderWidgetBlocks = (form, mainPath, activeSortableId, overSortableId) => {
  flushActiveDraftField()

  const currentBlocks = getMainBlocks(form, mainPath)
  const currentWidgetEntries = getWidgetEntries(currentBlocks)
  const currentWidgetBlocks = currentWidgetEntries.map(({ item }) => item)
  const fromIndex = currentWidgetEntries.findIndex((entry) => entry.sortableId === activeSortableId)
  const toIndex = currentWidgetEntries.findIndex((entry) => entry.sortableId === overSortableId)

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return
  }

  const nextWidgets = arrayMove(currentWidgetBlocks, fromIndex, toIndex)
  const widgetQueue = [...nextWidgets]
  const nextBlocks = currentBlocks.flatMap((block) => {
    if (!isFeaturedContentWidgetBlock(block)) {
      return [block]
    }

    const nextWidget = widgetQueue.shift()
    return nextWidget ? [nextWidget] : []
  })

  setMainBlocks(form, mainPath, [...nextBlocks, ...widgetQueue])
}

const CollectionSelector = ({ name, collections, disabled = false }) => {
  const form = useFormContext()

  const collectionOptions = useMemo(
    () =>
      buildFeaturedCollectionOptions(collections).map((option) => {
        if (!option.disabled) {
          return option
        }

        const collection = collections?.find((item) => +item.id === +option.id)
        const visibilityIssue = getCollectionStorefrontVisibilityIssue(collection)

        return {
          ...option,
          tooltip: getCollectionStorefrontVisibilityMessage(visibilityIssue),
          tooltipClassName: 'w-61',
          tooltipVariant: 'warning',
          end: (
            <CollectionStorefrontVisibilityIndicator
              visibilityIssue={visibilityIssue}
              withTooltip={false}
            />
          ),
        }
      }),
    [collections]
  )

  return (
    <form.AppField name={name}>
      {(field) => (
        <field.SelectInput
          options={collectionOptions}
          label={<b>Collection to Feature</b>}
          description="Choose the collection you want to spotlight."
          disabled={disabled}
        />
      )}
    </form.AppField>
  )
}

const normalizeProductSearch = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()

const ProductSearchSelectControl = ({ field, products, selectedProduct = null }) => {
  const inputId = useId()
  const labelId = useId()

  const productOptions = useMemo(() => {
    const productsById = new Map()
    const sourceProducts = Array.isArray(products) ? products : []

    sourceProducts
      .filter((product) => product?.id && product?.name)
      .forEach((product) => {
        productsById.set(String(product.id), product)
      })

    if (selectedProduct?.id && selectedProduct?.name) {
      productsById.set(String(selectedProduct.id), selectedProduct)
    }

    return Array.from(productsById.values()).map((product) => ({
      value: String(product.id),
      label: product.name,
    }))
  }, [products, selectedProduct])
  const selectedValue = field.state.value ? String(field.state.value) : ''
  const selectedOption = productOptions.find((option) => option.value === selectedValue) ?? null
  const [searchValue, setSearchValue] = useState(() => selectedOption?.label ?? '')
  const handleSelectedValueChange = (value) => {
    field.handleChange(value ? Number(value) : null)
    setSearchValue(productOptions.find((option) => option.value === value)?.label ?? '')
  }

  const searchableItems = useMemo(() => {
    const search = normalizeProductSearch(searchValue)
    const filteredOptions = search
      ? productOptions.filter((option) => normalizeProductSearch(option.label).includes(search))
      : productOptions
    const limitedOptions = filteredOptions.slice(0, MAX_SEARCHABLE_PRODUCT_OPTIONS)

    if (selectedOption && !limitedOptions.some((option) => option.value === selectedOption.value)) {
      return [selectedOption, ...limitedOptions.slice(0, MAX_SEARCHABLE_PRODUCT_OPTIONS - 1)]
    }

    return limitedOptions
  }, [productOptions, searchValue, selectedOption])

  return (
    <div className="flex w-full flex-col gap-y-2">
      <div className="inline-flex items-center">
        <Label id={labelId}>
          <b>Product Name</b>
        </Label>
      </div>
      <AutoComplete
        id={inputId}
        ariaLabelledBy={labelId}
        selectedValue={selectedValue}
        onSelectedValueChange={handleSelectedValueChange}
        searchValue={searchValue}
        onSearchValueChange={setSearchValue}
        items={searchableItems}
        placeholder={
          productOptions.length > 0 ? 'Search product name' : 'No products available to feature'
        }
        emptyMessage={
          productOptions.length > 0
            ? 'No products match that search.'
            : 'No products available to feature.'
        }
        readOnlyEmptyText="No product selected"
        disabled={productOptions.length === 0}
      />
      <FieldInfo />
    </div>
  )
}

const ProductSelector = ({ name, products, selectedProduct = null }) => {
  const form = useFormContext()

  return (
    <form.AppField name={name}>
      {(field) => (
        <ProductSearchSelectControl
          field={field}
          products={products}
          selectedProduct={selectedProduct}
        />
      )}
    </form.AppField>
  )
}

const LayoutSelector = ({ name }) => {
  const form = useFormContext()

  return (
    <form.AppField name={name}>
      {(field) => (
        <field.SelectInput
          options={[
            { id: 'carousel', name: 'Carousel' },
            { id: 'split', name: 'Split' },
          ]}
          label="Layout"
          description="Choose how this section appears on the storefront."
        />
      )}
    </form.AppField>
  )
}

const ImageAlignSelector = ({ name }) => {
  const form = useFormContext()

  return (
    <form.AppField name={name}>
      {(field) => (
        <field.SelectInput
          options={[
            { id: 'left', name: 'Left' },
            { id: 'right', name: 'Right' },
          ]}
          label="Image Position"
          description="Choose whether the image appears on the left or right."
        />
      )}
    </form.AppField>
  )
}

const FocusAlbumSelector = ({ name, albumOptions }) => {
  const form = useFormContext()

  if (albumOptions.length === 0) {
    return (
      <Typography variant="supporting" className="block">
        Add albums on the Gallery page before choosing a split layout album.
      </Typography>
    )
  }

  return (
    <form.AppField name={name}>
      {(field) => (
        <field.SelectInput
          options={albumOptions}
          label="Album to Feature"
          placeholder="Use top gallery album"
          readOnlyEmptyText="Top gallery album"
        />
      )}
    </form.AppField>
  )
}

const CarouselSizeSelector = ({ name }) => {
  const form = useFormContext()

  return (
    <form.AppField name={name}>
      {(field) => (
        <field.SelectInput
          options={[
            { id: 'sm', name: 'Small' },
            { id: 'lg', name: 'Large' },
          ]}
          defaultValue="sm"
          label="Carousel Image Size"
          description="Larger images stand out more, but fewer fit on screen."
        />
      )}
    </form.AppField>
  )
}

const TextField = ({ name, label, placeholder = '' }) => {
  const form = useFormContext()

  return (
    <PreviewDraftTextField
      form={form}
      name={name}
      label={label}
      placeholder={placeholder}
      flushOnUnmount={false}
    />
  )
}

const TextareaField = ({ name, label, placeholder = '' }) => {
  const form = useFormContext()

  return (
    <PreviewDraftTextareaField
      form={form}
      name={name}
      label={label}
      placeholder={placeholder}
      flushOnUnmount={false}
    />
  )
}

const FeaturedCollectionImageManager = ({
  collectionIdPath,
  collections,
  collectionMediaDraftController = null,
  className = '',
}) => {
  const form = useFormContext()

  return (
    <form.Subscribe selector={(state) => _get(state.values, collectionIdPath)}>
      {(collectionId) => {
        const selectedCollection = collections?.find(
          (collection) => +collection.id === +collectionId
        )

        if (!selectedCollection) {
          return null
        }

        return (
          <FeaturedCollectionImageControls
            key={selectedCollection.id}
            collection={selectedCollection}
            collectionMediaDraftController={collectionMediaDraftController}
            className={className}
          />
        )
      }}
    </form.Subscribe>
  )
}

const FeaturedCollectionImageActionButton = ({ label, icon: Icon, onClick, className = '' }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant="ghostMuted"
        size="icon-xs"
        className={className}
        aria-label={label}
        title={label}
        onClick={onClick}
      >
        <Icon className="size-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
)

const FeaturedCollectionImageControls = ({
  collection,
  collectionMediaDraftController = null,
  className = '',
}) => {
  const uploaderId = useId()
  const ability = useAbility()
  const { isReadOnly } = useIsReadOnly()
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const collectionMediaDrafts =
    collectionMediaDraftController?.drafts ?? EMPTY_COLLECTION_MEDIA_DRAFTS
  const media = getCollectionMediaWithDraft(collection, collectionMediaDrafts)
  const canEditCollections = ability.can('edit', 'collections')
  const canManageCollectionImage = typeof collectionMediaDraftController?.stageDraft === 'function'
  const isActionDisabled =
    isReadOnly ||
    !canEditCollections ||
    !canManageCollectionImage ||
    Boolean(collectionMediaDraftController?.isSavePending)
  const canEditCollectionImage = Boolean(media?.url) && canEditMedia(media)
  const hasDraft = hasCollectionMediaDraft(collectionMediaDrafts, collection.id)

  const stageCollectionMedia = (nextMedia) => {
    if (isActionDisabled) {
      return false
    }

    if (nextMedia && !getCollectionMediaId(nextMedia)) {
      toast.error('Collection image update failed', {
        description: 'The selected image is missing media details.',
      })
      return false
    }

    collectionMediaDraftController.stageDraft(collection, nextMedia ?? null)
    setIsUploaderOpen(false)
    return true
  }

  const handleSelectedAssets = (assets = []) => {
    const { selectedMedia, insertedCount, skippedCount } = selectFirstValidAssetMedia(assets, {
      uploadUsage: MediaUploadUsage.StorefrontHero.Value,
    })

    if (selectedMedia) {
      stageCollectionMedia(selectedMedia)
    }

    return {
      insertedCount,
      skippedCount,
    }
  }

  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex min-w-0 items-center gap-3 rounded-lg border border-border/70 bg-background/45 p-2">
        {media?.url ? (
          <UploadedMediaPreviewTile
            media={media}
            alt={media?.name || `${collection.name} collection image`}
            caption={media?.name}
            allowFocalPoint={!isActionDisabled}
            onFocalPointChange={stageCollectionMedia}
            wrapperClassName="size-20 shrink-0"
            frameClassName="aspect-square w-full rounded-lg bg-muted"
            imageClassName="h-full w-full rounded-lg object-cover"
          />
        ) : (
          <Dropzone asChild disabled={isActionDisabled}>
            <button
              type="button"
              data-raw-button-owner="dropzone-trigger"
              className="group flex size-20 shrink-0 flex-col items-center justify-center gap-1.5 p-2 text-center text-muted-foreground hover:text-foreground"
              disabled={isActionDisabled}
              onClick={() => setIsUploaderOpen(true)}
            >
              <Images className="size-4 shrink-0 text-interaction" aria-hidden="true" />
              <span className="block text-xs font-medium leading-tight text-foreground">
                {isActionDisabled ? 'No image' : 'Add image'}
              </span>
            </button>
          </Dropzone>
        )}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
          <Typography variant="label" className="block">
            Collection image
          </Typography>
          <Typography variant="supporting" className="block max-w-full truncate">
            {media?.url
              ? `${media?.name || 'Image selected'}${hasDraft ? ' (unsaved)' : ''}`
              : `No image selected${hasDraft ? ' (unsaved)' : ''}`}
          </Typography>
          {media?.url && !isActionDisabled ? (
            <div className="flex flex-wrap items-center gap-1">
              <FeaturedCollectionImageActionButton
                label="Change image"
                icon={ImagePlus}
                onClick={() => setIsUploaderOpen(true)}
              />
              {canEditCollectionImage ? (
                <FeaturedCollectionImageActionButton
                  label="Edit image"
                  icon={Pencil}
                  onClick={() => setIsEditorOpen(true)}
                />
              ) : null}
              <FeaturedCollectionImageActionButton
                label="Remove image"
                icon={X}
                className="text-status-destructive-foreground hover:text-status-destructive-foreground"
                onClick={() => stageCollectionMedia(null)}
              />
            </div>
          ) : null}
        </div>
      </div>
      <ImageUploader
        id={`featured-collection-image-${collection.id}-${uploaderId}`}
        open={isUploaderOpen}
        uploadUsage={MediaUploadUsage.StorefrontHero.Value}
        defaultUploadDestination={PhotoManagerDefaultUploadDestination.BannerImages}
        selectionMode={PhotoManagerSelectionMode.Single}
        onRequestClose={() => setIsUploaderOpen(false)}
        onSelectAssets={handleSelectedAssets}
        onUploadSuccess={(_, response) => {
          if (response?.body) {
            stageCollectionMedia(response.body)
          }
        }}
      />
      {media?.url && canEditCollectionImage ? (
        <LazyImageEditorDialog
          open={isEditorOpen}
          onOpenChange={setIsEditorOpen}
          media={media}
          uploadUsage={MediaUploadUsage.StorefrontHero.Value}
          attachToPhotoManager
          onEditComplete={(editedMedia) =>
            stageCollectionMedia(mergeEditedMedia(media, editedMedia))
          }
        />
      ) : null}
    </div>
  )
}

const FeaturedCollectionItem = ({
  item,
  blockIndex,
  sortableId,
  _props,
  collections,
  collectionMediaDraftController = null,
  onRemove,
  showDragHandle = true,
  showRemoveButton = true,
  quiet = false,
}) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false)

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly || !showDragHandle,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
  }
  const sortableAttributes = showDragHandle ? attributes : {}

  return (
    <Card
      className="relative w-full"
      size={quiet ? 'flush' : 'default'}
      surface={quiet ? 'bare' : 'default'}
      ref={setNodeRef}
      style={style}
      {...sortableAttributes}
    >
      <CardContent className={cn(quiet && 'px-0')}>
        {showRemoveButton ? (
          <Button
            disabled={isReadOnly}
            variant="ghost"
            className="absolute top-1 right-1"
            aria-label="Remove Featured Collection widget"
            onClick={onRemove}
          >
            <X />
          </Button>
        ) : null}
        <div
          className={cn(
            '@container/featured-collection-widget flex w-full items-center gap-3 @[36rem]/featured-collection-widget:gap-4',
            showRemoveButton && 'pr-10'
          )}
        >
          {showDragHandle ? (
            <Grip
              {...listeners}
              style={{ transition }}
              className="mt-1 cursor-grab touch-none active:cursor-grabbing @[36rem]/featured-collection-widget:mt-0"
            />
          ) : null}
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-4 @[32rem]/featured-collection-widget:grid-cols-2">
            <div className="@[32rem]/featured-collection-widget:col-span-2">
              <Typography variant="cardTitle" className="flex items-center gap-2">
                <Rows3 className="size-4" /> Featured Collection
              </Typography>
            </div>
            <div className="grid min-w-0 grid-cols-1 gap-4 @[32rem]/featured-collection-widget:col-span-2 @[32rem]/featured-collection-widget:grid-cols-2">
              <div className="min-w-0">
                <CollectionSelector
                  name={mainForPage(item.pageSlug, `${blockIndex}.collections.0.collection_id`)}
                  collections={collections}
                  disabled={!!collectionMediaDraftController?.isSavePending}
                />
                <form.Subscribe
                  selector={(state) =>
                    _get(
                      state.values,
                      mainForPage(item.pageSlug, `${blockIndex}.collections.0.collection_id`)
                    )
                  }
                >
                  {(collectionId) => {
                    const selectedCollection = collections?.find((c) => +c.id === +collectionId)
                    const visibilityIssue =
                      getCollectionStorefrontVisibilityIssue(selectedCollection)

                    if (!visibilityIssue) {
                      return null
                    }

                    return (
                      <Typography variant="supporting" className="mt-2 block">
                        {getCollectionStorefrontVisibilityMessage(visibilityIssue)}
                      </Typography>
                    )
                  }}
                </form.Subscribe>
              </div>
              <FeaturedCollectionImageManager
                collectionIdPath={mainForPage(
                  item.pageSlug,
                  `${blockIndex}.collections.0.collection_id`
                )}
                collections={collections}
                collectionMediaDraftController={collectionMediaDraftController}
              />
            </div>
            <div>
              <LayoutSelector name={_props('layout')} />
            </div>
            <div>
              <form.Subscribe selector={(state) => _get(state.values, _props('layout'))}>
                {(layout) => (
                  <>
                    {layout === 'carousel' ? (
                      <CarouselSizeSelector name={_props('size')} />
                    ) : (
                      <ImageAlignSelector name={_props('imageAlign')} />
                    )}
                  </>
                )}
              </form.Subscribe>
            </div>
            <div className="@[32rem]/featured-collection-widget:col-span-2">
              {isCustomizeOpen ? (
                <div className="space-y-4 pt-1">
                  <div className="grid grid-cols-1 gap-4 @[32rem]/featured-collection-widget:grid-cols-2">
                    <div className="@[32rem]/featured-collection-widget:col-span-2">
                      <TextField
                        name={_props('title')}
                        label="Collection Name"
                        placeholder="Use collection name"
                      />
                    </div>
                    <div className="@[32rem]/featured-collection-widget:col-span-2">
                      <TextareaField
                        name={_props('ctaText')}
                        label="Description"
                        placeholder="Add a short note about this collection"
                      />
                    </div>
                    <div className="@[32rem]/featured-collection-widget:col-span-2 @[32rem]/featured-collection-widget:max-w-sm">
                      <TextField
                        name={_props('ctaButtonText')}
                        label="Button Text"
                        placeholder="Shop collection"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="link"
                      size="inline"
                      onClick={() => {
                        flushActiveDraftField()
                        setIsCustomizeOpen(false)
                      }}
                    >
                      Hide
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  aria-expanded="false"
                  onClick={() => setIsCustomizeOpen(true)}
                >
                  Customize
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const SeasonalFeaturedCollectionFormBase = ({
  pageSlug = '/',
  isEnabled = true,
  collectionsOverride = null,
  collectionMediaDraftController = null,
}) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const { data: queriedCollections } = useCollections({ filters: designCollectionsFilters })
  const collections = collectionsOverride ?? queriedCollections
  const mainPath = nameForPage(pageSlug, 'main')
  const mainBlocks = useStore(form.store, (state) => {
    const blocks = _get(state.values, mainPath)
    return Array.isArray(blocks) ? blocks : []
  })
  const featuredCollectionEntry = getFirstFeaturedCollectionEntry(mainBlocks)

  useEffect(() => {
    if (!isEnabled || isReadOnly) {
      return
    }

    const currentBlocks = getMainBlocks(form, mainPath)
    const currentEntry = getFirstFeaturedCollectionEntry(currentBlocks)
    const nextBlocks = currentEntry ? [currentEntry.item] : [createNewFeaturedCollection()]

    if (
      currentBlocks.length === nextBlocks.length &&
      currentBlocks[0] === nextBlocks[0] &&
      currentBlocks[0]?.sort_order === 0
    ) {
      return
    }

    setMainBlocks(form, mainPath, nextBlocks)
  }, [form, isEnabled, isReadOnly, mainBlocks, mainPath])

  if (!isEnabled && !featuredCollectionEntry) {
    return (
      <Typography variant="supporting" className="block">
        Choose one collection to show first while this seasonal design is active. Your everyday
        sections stay below it.
      </Typography>
    )
  }

  if (!featuredCollectionEntry) {
    return (
      <Typography variant="supporting" className="block">
        Preparing the seasonal featured collection controls.
      </Typography>
    )
  }

  const { item, index, sortableId } = featuredCollectionEntry
  const itemWithPage = { ...item, pageSlug }
  const _props = (str) => mainForPage(pageSlug, `${index}.props.${str}`)

  return (
    <DndContext collisionDetection={closestCorners}>
      <SortableContext items={[sortableId]}>
        <FeaturedCollectionItem
          collections={collections}
          item={itemWithPage}
          blockIndex={index}
          sortableId={sortableId}
          _props={_props}
          onRemove={() => setMainBlocks(form, mainPath, [])}
          collectionMediaDraftController={collectionMediaDraftController}
          showDragHandle={false}
          showRemoveButton={false}
          quiet
        />
      </SortableContext>
    </DndContext>
  )
}

export const SeasonalFeaturedCollectionForm = withSuspense(SeasonalFeaturedCollectionFormBase)

const FeaturedProductItem = ({ item, blockIndex, sortableId, _props, products, onRemove }) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
  }

  return (
    <Card className="relative w-full" ref={setNodeRef} style={style} {...attributes}>
      <CardContent>
        <Button
          disabled={isReadOnly}
          variant="ghost"
          className="absolute right-1 top-1"
          aria-label="Remove Featured Product widget"
          onClick={onRemove}
        >
          <X />
        </Button>
        <div className="@container/featured-product-widget flex w-full items-center gap-3 @[36rem]/featured-product-widget:gap-4">
          <Grip
            {...listeners}
            style={{ transition }}
            className="mt-1 cursor-grab touch-none active:cursor-grabbing @[36rem]/featured-product-widget:mt-0"
          />
          <div className="grid w-full min-w-0 grid-cols-1 gap-4 @[24rem]/featured-product-widget:grid-cols-2 @[36rem]/featured-product-widget:w-1/2">
            <div className="@[24rem]/featured-product-widget:col-span-2">
              <Typography variant="cardTitle" className="mb-3 flex items-center gap-2">
                <ShoppingBag className="size-4" /> Featured Product
              </Typography>
              <Typography variant="supporting" className="mb-3 block">
                Spotlight one product inside Featured Content.
              </Typography>
              <ProductSelector
                name={mainForPage(item.pageSlug, `${blockIndex}.products.0.product_id`)}
                products={products}
                selectedProduct={item?.products?.[0]?.product ?? null}
              />
            </div>
            <div>
              <ImageAlignSelector name={_props('imageAlign')} />
            </div>
            <div>
              <TextField name={_props('title')} label="Title" placeholder="Use product name" />
            </div>
            <div className="@[24rem]/featured-product-widget:col-span-2">
              <TextareaField
                name={_props('ctaText')}
                label="Message"
                placeholder="Add a short note about this product"
              />
            </div>
            <div>
              <TextField
                name={_props('ctaButtonText')}
                label="Button Text"
                placeholder="Shop Now"
              />
            </div>
          </div>
          <div className="ml-auto mr-8 hidden h-full @[36rem]/featured-product-widget:block">
            <form.Subscribe
              selector={(state) =>
                _get(
                  state.values,
                  mainForPage(item.pageSlug, `${blockIndex}.products.0.product_id`)
                )
              }
            >
              {(productId) => {
                const media = products?.find((product) => +product.id === +productId)?.media?.[0]
                return (
                  <>
                    {media ? (
                      <ImageThumbnail media={media} className="h-auto w-40" />
                    ) : (
                      <div className="flex h-32 w-40 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        No Media
                      </div>
                    )}
                  </>
                )
              }}
            </form.Subscribe>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const GalleryWidgetItem = ({ sortableId, _props, albumOptions, onRemove }) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
  }

  return (
    <Card className="relative w-full" ref={setNodeRef} style={style} {...attributes}>
      <CardContent>
        <Button
          disabled={isReadOnly}
          variant="ghost"
          className="absolute right-1 top-1"
          aria-label="Remove Gallery Widget"
          onClick={onRemove}
        >
          <X />
        </Button>
        <div className="@container/gallery-widget flex w-full items-center gap-3 pr-10 @[20rem]/gallery-widget:gap-4">
          <Grip
            {...listeners}
            style={{ transition }}
            className="mt-1 cursor-grab touch-none active:cursor-grabbing @[20rem]/gallery-widget:mt-0"
          />
          <div className="grid w-full min-w-0 grid-cols-1 gap-4 @[30rem]/gallery-widget:grid-cols-2">
            <div className="@[30rem]/gallery-widget:col-span-2">
              <Typography variant="cardTitle" className="flex items-center gap-2">
                <Images className="size-4" /> Gallery Widget
              </Typography>
              <Typography variant="supporting" className="mt-1 block">
                Shows enabled gallery albums with photos. Hidden from shoppers until an album has
                photos.
              </Typography>
            </div>
            <div>
              <LayoutSelector name={_props('layout')} />
            </div>
            <form.Subscribe selector={(state) => _get(state.values, _props('layout'))}>
              {(layout) =>
                layout === 'carousel' ? (
                  <div>
                    <CarouselSizeSelector name={_props('size')} />
                  </div>
                ) : layout === 'split' ? (
                  <>
                    <div>
                      <ImageAlignSelector name={_props('imageAlign')} />
                    </div>
                    <div className="@[30rem]/gallery-widget:col-span-2">
                      <FocusAlbumSelector
                        name={_props('focusAlbumId')}
                        albumOptions={albumOptions}
                      />
                    </div>
                  </>
                ) : null
              }
            </form.Subscribe>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const ContactUsWidgetItem = ({ sortableId, _props, onRemove }) => {
  const { isReadOnly } = useIsReadOnly()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
  }

  return (
    <Card className="relative w-full" ref={setNodeRef} style={style} {...attributes}>
      <CardContent>
        <Button
          disabled={isReadOnly}
          variant="ghost"
          className="absolute right-1 top-1"
          aria-label="Remove Contact Us widget"
          onClick={onRemove}
        >
          <X />
        </Button>
        <div className="@container/contact-us-widget flex w-full items-center gap-3 pr-10 @[20rem]/contact-us-widget:gap-4">
          <Grip
            {...listeners}
            style={{ transition }}
            className="mt-1 cursor-grab touch-none active:cursor-grabbing @[20rem]/contact-us-widget:mt-0"
          />
          <div className="grid w-full min-w-0 grid-cols-1 gap-4 @[20rem]/contact-us-widget:grid-cols-2">
            <div className="@[20rem]/contact-us-widget:col-span-2">
              <Typography variant="cardTitle" className="flex items-center gap-2">
                <MessageCircleHeart className="size-4" /> Contact Us
              </Typography>
              <Typography variant="supporting" className="mt-1 block">
                Invite shoppers to reach out from your Contact page.
              </Typography>
            </div>
            <div>
              <TextField
                name={_props('title')}
                label="Title"
                placeholder={DEFAULT_CONTACT_US_WIDGET_TITLE}
              />
            </div>
            <div>
              <TextField
                name={_props('ctaButtonText')}
                label="Button Text"
                placeholder={DEFAULT_CONTACT_US_WIDGET_BUTTON_TEXT}
              />
            </div>
            <div className="@[20rem]/contact-us-widget:col-span-2">
              <TextareaField
                name={_props('ctaText')}
                label="Message"
                placeholder={DEFAULT_CONTACT_US_WIDGET_MESSAGE}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const ReviewsWidgetItem = ({ sortableId, _props, onRemove }) => {
  const { isReadOnly } = useIsReadOnly()
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId,
    disabled: isReadOnly,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : 1,
  }

  return (
    <Card className="relative w-full" ref={setNodeRef} style={style} {...attributes}>
      <CardContent>
        <Button
          disabled={isReadOnly}
          variant="ghost"
          className="absolute right-1 top-1"
          onClick={onRemove}
        >
          <X />
        </Button>
        <div className="@container/reviews-widget flex w-full items-center gap-3 pr-10 @[20rem]/reviews-widget:gap-4">
          <Grip
            {...listeners}
            style={{ transition }}
            className="mt-1 cursor-grab touch-none active:cursor-grabbing @[20rem]/reviews-widget:mt-0"
          />
          <div className="grid w-full min-w-0 grid-cols-1 gap-4">
            <div>
              <Typography variant="cardTitle" className="flex items-center gap-2">
                <Star className="size-4" /> Reviews Widget
              </Typography>
              <Typography variant="supporting" className="mt-1 block">
                Shows approved customer reviews. Hidden from shoppers until at least one review is
                approved.
              </Typography>
            </div>
            <div>
              <TextField
                name={_props('title')}
                label="Title"
                placeholder={DEFAULT_REVIEWS_WIDGET_TITLE}
              />
            </div>
            <div>
              <TextareaField
                name={_props('ctaText')}
                label="Message"
                placeholder={DEFAULT_REVIEWS_WIDGET_MESSAGE}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const AddWidgetDropdown = ({
  isReadOnly,
  canAddFeaturedCollection,
  canAddGalleryWidget,
  canAddContactUsWidget,
  canAddReviewsWidget,
  galleryWidgetAllowed,
  contactUsWidgetAllowed,
  reviewsWidgetAllowed,
  onAddCollection,
  onAddProduct,
  onAddGalleryWidget,
  onAddContactUsWidget,
  onAddReviewsWidget,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button type="button" className="w-full md:w-fit" disabled={isReadOnly}>
        Add Widget
        <PlusIcon />
        <ChevronDown className="size-4" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" size="md">
      <DropdownMenuItem disabled={!canAddFeaturedCollection} onSelect={onAddCollection}>
        <Rows3 className="size-4" />
        Featured Collection
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={onAddProduct}>
        <ShoppingBag className="size-4" />
        Featured Product
      </DropdownMenuItem>
      {galleryWidgetAllowed ? (
        <DropdownMenuItem disabled={!canAddGalleryWidget} onSelect={onAddGalleryWidget}>
          <Images className="size-4" />
          Gallery Widget
        </DropdownMenuItem>
      ) : null}
      {contactUsWidgetAllowed ? (
        <DropdownMenuItem disabled={!canAddContactUsWidget} onSelect={onAddContactUsWidget}>
          <MessageCircleHeart className="size-4" />
          Contact Us
        </DropdownMenuItem>
      ) : null}
      {reviewsWidgetAllowed ? (
        <DropdownMenuItem disabled={!canAddReviewsWidget} onSelect={onAddReviewsWidget}>
          <Star className="size-4" />
          Reviews Widget
        </DropdownMenuItem>
      ) : null}
    </DropdownMenuContent>
  </DropdownMenu>
)

const FeaturedContentForm = ({
  pageSlug = '/',
  allowGalleryWidget = null,
  collectionsOverride = null,
  collectionMediaDraftController = null,
}) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()

  const { data: queriedCollections } = useCollections({ filters: designCollectionsFilters })
  const collections = collectionsOverride ?? queriedCollections
  const { data: products } = useProducts({ filters: designProductsFilters })
  const pageConfig = pageConfigFor(pageSlug)
  const galleryWidgetAllowed = allowGalleryWidget ?? pageConfig.allowGalleryWidget
  const contactUsWidgetAllowed = pageConfig.allowContactUsWidget
  const reviewsWidgetAllowed = pageConfig.allowReviewsWidget
  const mainPath = nameForPage(pageSlug, 'main')
  const mainBlocks = useStore(form.store, (state) => {
    const blocks = _get(state.values, mainPath)
    return Array.isArray(blocks) ? blocks : []
  })
  const galleryAlbumOptions = useStore(form.store, (state) => {
    const galleryMainBlocks = _get(state.values, 'pages./gallery.blocks.main')
    const galleryAlbumBlock = Array.isArray(galleryMainBlocks)
      ? galleryMainBlocks.find((block) => block?.type === 'album')
      : null

    return getGalleryAlbumOptions(galleryAlbumBlock?.albums)
  })
  const widgetEntries = getWidgetEntries(mainBlocks)
  const widgetBlocks = widgetEntries.map(({ item }) => item)
  const featuredCollectionCount = widgetBlocks.filter(isFeaturedCollectionWidget).length
  const galleryWidgetCount = widgetBlocks.filter(isGalleryWidget).length
  const contactUsWidgetCount = widgetBlocks.filter(isContactUsWidget).length
  const reviewsWidgetCount = widgetBlocks.filter(isReviewsWidget).length
  const canAddFeaturedCollection =
    pageConfig.maxFeaturedCollectionWidgets === null ||
    featuredCollectionCount < pageConfig.maxFeaturedCollectionWidgets
  const canAddGalleryWidget = galleryWidgetAllowed && galleryWidgetCount < 1
  const canAddContactUsWidget = contactUsWidgetAllowed && contactUsWidgetCount < 1
  const canAddReviewsWidget = reviewsWidgetAllowed && reviewsWidgetCount < 1
  const featuredCollectionLimitMessage = getFeaturedCollectionLimitMessage(pageConfig)

  const handleAddProduct = () => {
    appendWidgetBlock(form, mainPath, createNewFeaturedProduct())
  }

  const handleAddCollection = () => {
    appendWidgetBlock(form, mainPath, createNewFeaturedCollection(), (blocks) =>
      canAddFeaturedCollectionToBlocks(blocks, pageConfig.maxFeaturedCollectionWidgets)
    )
  }

  const handleAddGalleryWidget = () => {
    appendWidgetBlock(form, mainPath, createNewGalleryWidget(), (blocks) =>
      galleryWidgetAllowed ? canAddOnePerPageWidgetToBlocks(blocks, isGalleryWidget) : false
    )
  }

  const handleAddContactUsWidget = () => {
    appendWidgetBlock(form, mainPath, createNewContactUsWidget(), (blocks) =>
      contactUsWidgetAllowed ? canAddOnePerPageWidgetToBlocks(blocks, isContactUsWidget) : false
    )
  }

  const handleAddReviewsWidget = () => {
    appendWidgetBlock(form, mainPath, createNewReviewsWidget(), (blocks) =>
      reviewsWidgetAllowed ? canAddOnePerPageWidgetToBlocks(blocks, isReviewsWidget) : false
    )
  }

  const handleRemoveWidget = (sortableId) => {
    removeWidgetBlock(form, mainPath, sortableId)
  }

  const handleWidgetDragEnd = ({ active, over }) => {
    if (!over || active?.id === over?.id) {
      return
    }

    reorderWidgetBlocks(form, mainPath, active.id, over.id)
  }

  return (
    <div className="flex flex-col gap-y-4">
      <div>
        <form.AppField name={mainPath} mode="array">
          {(field) => (
            <div className="flex flex-col gap-4">
              <AddWidgetDropdown
                isReadOnly={isReadOnly}
                canAddFeaturedCollection={canAddFeaturedCollection}
                canAddGalleryWidget={canAddGalleryWidget}
                canAddContactUsWidget={canAddContactUsWidget}
                canAddReviewsWidget={canAddReviewsWidget}
                galleryWidgetAllowed={galleryWidgetAllowed}
                contactUsWidgetAllowed={contactUsWidgetAllowed}
                reviewsWidgetAllowed={reviewsWidgetAllowed}
                onAddCollection={handleAddCollection}
                onAddProduct={handleAddProduct}
                onAddGalleryWidget={handleAddGalleryWidget}
                onAddContactUsWidget={handleAddContactUsWidget}
                onAddReviewsWidget={handleAddReviewsWidget}
              />
              {!canAddFeaturedCollection && featuredCollectionLimitMessage ? (
                <Typography variant="supporting" className="block">
                  {featuredCollectionLimitMessage}
                </Typography>
              ) : null}
              {galleryWidgetAllowed && !canAddGalleryWidget ? (
                <Typography variant="supporting" className="block">
                  {pageConfig.label} can include one Gallery Widget.
                </Typography>
              ) : null}
              {contactUsWidgetAllowed && !canAddContactUsWidget ? (
                <Typography variant="supporting" className="block">
                  {pageConfig.label} can include one Contact Us widget.
                </Typography>
              ) : null}
              {reviewsWidgetAllowed && !canAddReviewsWidget ? (
                <Typography variant="supporting" className="block">
                  {pageConfig.label} can include one Reviews Widget.
                </Typography>
              ) : null}
              <div className="flex flex-col gap-2">
                {widgetEntries.length > 0 ? (
                  <DndContext collisionDetection={closestCorners} onDragEnd={handleWidgetDragEnd}>
                    <SortableContext items={widgetEntries.map((entry) => entry.sortableId)}>
                      {widgetEntries.map(({ item, index, sortableId }) => {
                        const itemWithPage = { ...item, pageSlug }
                        const _props = (str) => mainForPage(pageSlug, `${index}.props.${str}`)

                        if (item?.type === GALLERY_WIDGET_BLOCK_TYPE) {
                          return (
                            <GalleryWidgetItem
                              key={sortableId}
                              sortableId={sortableId}
                              _props={_props}
                              albumOptions={galleryAlbumOptions}
                              onRemove={() => handleRemoveWidget(sortableId)}
                            />
                          )
                        }

                        if (item?.type === FEATURED_PRODUCT_BLOCK_TYPE) {
                          return (
                            <FeaturedProductItem
                              key={sortableId}
                              item={itemWithPage}
                              blockIndex={index}
                              sortableId={sortableId}
                              _props={_props}
                              products={products}
                              onRemove={() => handleRemoveWidget(sortableId)}
                            />
                          )
                        }

                        if (item?.type === CONTACT_US_WIDGET_BLOCK_TYPE) {
                          return (
                            <ContactUsWidgetItem
                              key={sortableId}
                              sortableId={sortableId}
                              _props={_props}
                              onRemove={() => handleRemoveWidget(sortableId)}
                            />
                          )
                        }

                        if (item?.type === CUSTOMER_REVIEWS_WIDGET_BLOCK_TYPE) {
                          return (
                            <ReviewsWidgetItem
                              key={sortableId}
                              sortableId={sortableId}
                              _props={_props}
                              onRemove={() => handleRemoveWidget(sortableId)}
                            />
                          )
                        }

                        return (
                          <FeaturedCollectionItem
                            collections={collections}
                            key={sortableId}
                            item={itemWithPage}
                            blockIndex={index}
                            sortableId={sortableId}
                            _props={_props}
                            collectionMediaDraftController={collectionMediaDraftController}
                            onRemove={() => handleRemoveWidget(sortableId)}
                          />
                        )
                      })}
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Typography
                    variant="supporting"
                    className={cn('block', field.state.meta?.errors?.length && 'mt-1')}
                  >
                    {pageConfig.emptyState}
                  </Typography>
                )}
              </div>
            </div>
          )}
        </form.AppField>
      </div>
    </div>
  )
}

export default withSuspense(FeaturedContentForm)
